import { Stream } from '@engoo/realtime-protocol/stream';
import React, {
  useRef,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { stringChangeToOps } from 'app/services/rtc/sync/string';
import { INotesAction } from '@engoo/realtime-protocol/notes';
import { insertTextAt, deleteText } from 'app/components/forms/inputUtils';
import { Events } from 'app/services/rtc/sync/documentmanagers';
import { NotesDocumentManagerState, NotesDocumentSpec } from 'app/models/rtc/notes/documentSpec';
import useDocumentManager from 'app/containers/rtc/DocumentProvider/useDocumentManager';
import { throwingMethods } from 'app/utils/throwingMethods';
import { SyncState } from 'app/models/rtc/sync/constants';

export interface INotesContext extends Pick<
  NotesDocumentManagerState, 'metadata' | 'syncState'
> {
  readonly value: NonNullable<NotesDocumentManagerState['documentState']>;
  readonly isBlocked: boolean;
  readonly onLocalChange: (newValue: string) => void;
  readonly subscribe: (handler: RemoteActionHandler) => void;
  readonly unsubscribe: (handler: RemoteActionHandler) => void;
}

export const NotesContext = React.createContext<INotesContext>({
  value: '',
  syncState: SyncState.closed,
  metadata: undefined,
  isBlocked: false,
  ...throwingMethods(['onLocalChange', 'subscribe', 'unsubscribe']),
});

type RemoteActionHandler = (payload: { state: string, actions: INotesAction[] }) => void;

export const NotesDocumentProvider: React.FC<Record<string, unknown>> = props => {
  const store = useDocumentManager(Stream.Notes, NotesDocumentSpec);
  const { manager, metadata, documentState, syncState, isBlocked } = store;

  const value = documentState || '';

  const onLocalChange = useCallback((newValue: string) => {
    if (!manager) {
      throw new Error(
        `Notes Context is unable to process local actions: manager not present.
          Did you forget the context provider?`,
      );
    }

    if (value === newValue) return;

    manager.addLocalAction({ op: stringChangeToOps(value, newValue) });
  }, [manager, value]);

  const subscriptionActions = useMemo(() => {
    if (!manager) {
      return {
        subscribe: () => {},
        unsubscribe: () => {},
      };
    }

    return {
      subscribe: (handler: RemoteActionHandler) => {
       /**
        * We want this handler to run before the state was updated,
        * otherwise the updates are applied onto already updated state
        * which leads to unexpected behavior (letter duplication etc)
        */
        manager.prependListener(Events.REMOTE_UPDATED, handler);
      },
      unsubscribe: (handler: RemoteActionHandler) => {
        manager.removeListener(Events.REMOTE_UPDATED, handler);
      },
    };
  }, [manager]);

  const contextValue: INotesContext = useMemo(() => ({
    ...subscriptionActions,
    metadata,
    onLocalChange,
    value,
    isBlocked,
    syncState,
  }), [
    value, syncState, subscriptionActions,
    onLocalChange, isBlocked, metadata,
  ]);

  return (
    <NotesContext.Provider value={contextValue}>
      {props.children}
    </NotesContext.Provider>
  );
};

export const useNotesContext = () => useContext(NotesContext);

/**
 * `useNotesInput` subscribes to Notes's stream remote actions
 * and applies operational transformations (OTs) on a text input
 * field.
 *
 * It provides `setInputRef`:
 *
 * - `setInputRef`: is supposed to be passed as `ref={inputRef}`
 *   attribute to a `<TextArea>` component or any that supports
 *   `insertTextAt(position, text)` and `deleteText(position, length)`
 *   methods.
 */
export const useNotesInput = () => {
  const {
    value, isBlocked, syncState, onLocalChange,
    subscribe, unsubscribe,
  } = useNotesContext();
  const inputRef = useRef<HTMLTextAreaElement | null>(null);

  const setInputRef = useCallback((node: HTMLTextAreaElement | null) => {
    inputRef.current = node;
  }, [inputRef]);

  const onAction: RemoteActionHandler = useCallback(({ actions }) => {
    const { current: input } = inputRef;

    if (!input) {
      return;
    }

    actions.forEach(({ op }) => {
      let pos = 0;

      op.forEach(c => {
        switch (typeof c) {
          case 'number':
            pos += c;
            break;
          case 'string':
            insertTextAt(input, pos, c);
            pos += c.length;
            break;
          case 'object':
            deleteText(input, pos, c.d);
            break;
          default:
            throw new Error(`Invalid operation stream: ${JSON.stringify(op)}`);
        }
      });
    });
  }, [inputRef]);


  useEffect(() => {
    subscribe(onAction);

    return () => {
      unsubscribe(onAction);
    };
  }, [subscribe, unsubscribe, onAction]);

  return {
    isBlocked,
    setInputRef,
    value,
    syncState,
    onLocalChange,
  };
};
